import { ArrowLeftIcon, IconColor, IconSize } from '@axon-enterprise/icon';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';

/** Path to the Axon logo icon in the installation guide assets */
const LOGO_PATH = '/assets/images/installation-guide/logo-axon-icon.png';

interface WizardHeaderProps {
  /** Current step number (1-based), or null if step indicator should be hidden */
  stepNumber: number | null;
  /** Total number of steps */
  totalSteps: number;
  /** Whether to show the back button */
  showBack: boolean;
  /** Callback when back button is clicked */
  onBack: () => void;
}

/**
 * WizardHeader - Header component for the installation guide
 * 
 * Displays:
 * - Back button (left arrow in green square) when showBack is true
 * - Axon logo (centered)
 * - Step indicator text (e.g., "Step 1 of 9") when stepNumber is provided
 */
export const WizardHeader: React.FC<WizardHeaderProps> = ({
  stepNumber,
  totalSteps,
  showBack,
  onBack,
}) => {
  return (
    <Box
      sx={{
        bgcolor: 'white',
        p: { xs: 2, sm: 3 },
        pb: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      {/* Back button - positioned absolutely on the left */}
      {showBack && (
        <IconButton
          onClick={onBack}
          aria-label={__('Go back')}
          sx={{
            position: 'absolute',
            left: { xs: 8, sm: 16 },
            top: { xs: 8, sm: 16 },
            bgcolor: 'background.paper',
            boxShadow: 1,
            borderRadius: 1,
            width: 40,
            height: 40,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <ArrowLeftIcon size={IconSize.XS} color={IconColor.primary} />
        </IconButton>
      )}

      {/* Logo */}
      <Box
        component="img"
        src={LOGO_PATH}
        alt={__('Axon')}
        sx={{
          height: { xs: 32, sm: 40 },
          width: 'auto',
          mb: 1,
        }}
      />

      {/* Step indicator */}
      {stepNumber !== null && (
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {__('Step $[current] of $[total]', {
            current: stepNumber,
            total: totalSteps,
          })}
        </Typography>
      )}
    </Box>
  );
};
